import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { subscribe } from "../../lib/walletEvents";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Globally-mounted modal that opens whenever the axios interceptor sees a
// 402 with body.error="subscription_lapsed". This is the Vikram-bug fix
// surfaced to the user — no more silent 403s with no path forward.
export default function SubscriptionLapsedModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const panelRef = useRef(null);
  const close = () => setOpen(false);
  useDialogDismiss({ open, onClose: close, ref: panelRef });

  useEffect(() => {
    return subscribe("subscription_lapsed", (payload) => {
      setData(payload);
      setOpen(true);
    });
  }, []);

  if (!open || !data) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Subscription not active"
        className="bg-bg-primary border border-border-default p-5 sm:p-8 w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-text-secondary hover:text-text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary">Subscription not active</h3>
            <p className="text-sm text-text-secondary mt-1">
              {data.message || "Reactivate your subscription to continue."}
            </p>
            <p className="text-xs text-text-secondary mt-3">
              Your data is safe — you'll keep read access while your subscription is paused.
            </p>
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm text-text-secondary hover:bg-bg-secondary/40"
          >
            Later
          </button>
          <Link
            to={data.action_url || "/subscriptions"}
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm bg-button-primary text-white hover:opacity-90"
          >
            Reactivate
          </Link>
        </div>
      </div>
    </div>
  );
}
